import { useState, useEffect } from "react";
import { HardHat, ChevronLeft, ChevronRight } from "lucide-react";
import { getActiveMinersAPI } from "../../api/miningApi";

export default function MiningActiveMiners() {
  const [miners, setMiners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(15);
  const [totalPages, setTotalPages] = useState(1);
  const [totalMiners, setTotalMiners] = useState(0);

  useEffect(() => {
    fetchMiners();
  }, [page]);

  const fetchMiners = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getActiveMinersAPI(page, pageSize);
      if (response.success) {
        setMiners(response.data.data || response.data);
        setTotalMiners(response.data.total_rows || response.data.length || 0);
        setTotalPages(Math.ceil((response.data.total_rows || response.data.length || 0) / pageSize));
      } else {
        setError("Failed to fetch active miners");
      }
    } catch (err) {
      setError("Failed to fetch active miners");
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const renderPagination = () => {
    return Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => {
      if (p === 1 || p === totalPages || (p >= page - 1 && p <= page + 1)) {
        return (
          <button
            key={p}
            onClick={() => setPage(p)}
            className={`h-8 w-8 rounded text-xs font-medium transition-colors ${
              p === page
                ? "bg-ginox-green/20 border border-ginox-green/60 text-ginox-green"
                : "border border-border/70 bg-white/[0.02] text-muted-foreground hover:bg-white/[0.05] hover:text-foreground"
            }`}
          >
            {p}
          </button>
        );
      }
      if (p === page - 2 || p === page + 2) {
        return (
          <span key={p} className="h-8 w-8 flex items-center justify-center text-muted-foreground text-xs">
            ...
          </span>
        );
      }
      return null;
    });
  };

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-400 text-base mb-4">{error}</p>
          <button
            onClick={fetchMiners}
            className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
          <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
          Mining
        </div>
        <h1 className="font-serif italic text-[45px] leading-[1.02] text-foreground">Active <span className="text-gradient-ginox">Miners</span></h1>
        <p className="text-sm text-muted-foreground">
          {loading ? "Loading..." : `${totalMiners} miners currently active`}
        </p>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-separate border-spacing-0">
          <thead>
            <tr>
              {["Name", "Username", "Status", "Mining", "Weekly", "Wallet Address", "Discord ID", "Last Active"].map((h) => (
                <th key={h} className="border-b border-border/70 px-4 py-3 text-left text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} className="py-12 text-center text-sm text-muted-foreground">Loading...</td>
              </tr>
            ) : miners.length > 0 ? (
              miners.map((miner) => (
                <tr key={miner._id} className="group hover:bg-white/[0.02] transition-colors">
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground">{miner.name}</td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-muted-foreground">@{miner.username}</td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm">
                    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                      {miner.accountStatus}
                    </span>
                  </td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{miner.miningCount}</td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{miner.weeklyMiningCount}</td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-muted-foreground font-mono">
                    {miner.walletAddress ? (
                      <span title={miner.walletAddress}>
                        {miner.walletAddress.slice(0, 6)}...{miner.walletAddress.slice(-4)}
                      </span>
                    ) : (
                      <span className="text-muted-foreground/40">-</span>
                    )}
                  </td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-muted-foreground">
                    {miner.discordId || <span className="text-muted-foreground/40">-</span>}
                  </td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">
                    {new Date(miner.lastAppOpen).toLocaleDateString()}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={8} className="py-12 text-center text-sm text-muted-foreground">No active miners found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col items-center gap-3 md:flex-row md:items-center md:justify-between">
          <p className="text-xs text-muted-foreground">
            Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, totalMiners)} of {totalMiners} miners
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              className="h-8 w-8 rounded border border-border/70 bg-white/[0.02] text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
            {renderPagination()}
            <button
              onClick={() => setPage(page + 1)}
              disabled={page === totalPages}
              className="h-8 w-8 rounded border border-border/70 bg-white/[0.02] text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
